import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as DEFAULTS from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Merge the raw editable document (from the database) over the code defaults.
export const buildContent = (remote) => {
  const site = remote || {};
  const episodes = (site.episodes?.length ? site.episodes : DEFAULTS.EPISODES).map((e) => ({ ...e }));
  const latestNum = site.latestEpisodeNum ?? DEFAULTS.LATEST_EPISODE.num;
  const latestBase = episodes.find((e) => e.num === latestNum) || episodes[episodes.length - 1];
  const sections = site.sections?.length ? site.sections : DEFAULTS.SITE.sections;

  return {
    SOCIALS: { ...DEFAULTS.SOCIALS, ...(site.socials || {}) },
    CHARACTERS: site.characters?.length ? site.characters : DEFAULTS.CHARACTERS,
    EPISODES: episodes,
    LATEST_EPISODE: { ...latestBase, synopsis: site.latestSynopsis ?? DEFAULTS.LATEST_EPISODE.synopsis },
    LESSONS: site.lessons?.length ? site.lessons : DEFAULTS.LESSONS,
    QUOTES: site.quotes?.length ? site.quotes : DEFAULTS.QUOTES,
    latestEpisodeNum: latestNum,
    SITE: {
      hero: { ...DEFAULTS.SITE.hero, ...(site.hero || {}) },
      marquee: site.marquee?.length ? site.marquee : DEFAULTS.SITE.marquee,
      sound: { ...DEFAULTS.SITE.sound, ...(site.sound || {}) },
      story: { ...DEFAULTS.SITE.story, ...(site.story || {}) },
      family: { ...DEFAULTS.SITE.family, ...(site.family || {}) },
      episodesSide: site.episodesSide ?? DEFAULTS.SITE.episodesSide,
      lessonsHover: site.lessonsHover ?? DEFAULTS.SITE.lessonsHover,
      watch: { ...DEFAULTS.SITE.watch, ...(site.watch || {}) },
      newsletter: { ...DEFAULTS.SITE.newsletter, ...(site.newsletter || {}) },
      creator: { ...DEFAULTS.SITE.creator, ...(site.creator || {}) },
    },
    SECTIONS: sections,
  };
};

const ContentContext = createContext(null);

export const ContentProvider = ({ children }) => {
  const [remote, setRemote] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then(({ data }) => setRemote(data?.content || null))
      .catch(() => setRemote(null));
  }, []);

  const value = useMemo(() => buildContent(remote), [remote]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => useContext(ContentContext);

// Chapter number + label follow the section's position and name in the (reorderable) sections list.
export const useSectionMeta = (id) => {
  const { SECTIONS } = useContent();
  const visible = SECTIONS.filter((s) => s.visible !== false);
  const idx = visible.findIndex((s) => s.id === id);
  const sec = idx >= 0 ? visible[idx] : SECTIONS.find((s) => s.id === id);
  return { number: String((idx >= 0 ? idx : 0) + 1).padStart(2, "0"), label: sec?.label || id };
};
